import { Input } from "../components/Input";
import GlobalPhotosLibraryModal from "../components/GlobalPhotosLibraryModal";
import { Field, Label, Switch } from "@headlessui/react";
import {
  Cog6ToothIcon,
  LockClosedIcon,
  LockOpenIcon,
} from "@heroicons/react/24/outline";
import { SESSION_TIME_BTNS } from "./utils/formConstants";
import ButtonGroup from "./components/ButtonGroup";
import StepperInput from "./components/StepperInput";
import CostInput from "./components/CostInput";
import PriceScheduleInput from "./components/PriceScheduleInput";
import PhotosInput from "./components/PhotosInput";
import GroupNamesInput from "./components/GroupNamesInput";
import CapacityInput from "./components/CapacityInput";
import RoomKindInput from "./components/RoomKindInput";
import MeetupEventIdInput from "./components/MeetupEventIdInput";
import ListingVariantSection from "./components/ListingVariantSection";
import { VARIANT_ID } from "./utils/listingVariants";
import VendorSelector from "./components/VendorSelector";
import { useNavigate } from "react-router-dom";
import { buildDateChangeMessage } from "./formSectionHelpers";
import { buildEventDuplicateState } from "./utils/duplicateEvent";
import { countEventGuests } from "./utils/eventGuests";
import { eventAccessFor } from "./utils/eventHosts";
import { useEventForm } from "./useEventForm";
import { useRecreateListing } from "./useRecreateListing";
import {
  PlacesDropdown,
  VenueMapPreview,
  HostPinInput,
  EventDateInput,
  StatusCheckboxes,
  FormActions,
  EventUrlInput,
  EventVideoControl,
  CheckInControl,
} from "./FormSectionParts";
import { HostPinPickerModal, DateChangeNotifyModal } from "./FormSectionModals";
import CancelEventWizard from "./CancelEventWizard";

export const FormSection = ({ sourceSuggestionId = null, sourceRequestId = null, photoGateBlocked = false }) => {
  const {
    loading,
    event,
    globalState,
    eventData,
    handleChange,
    handleNestedChange,
    handleCostCommit,
    handlePhotoChange,
    removePhoto,
    handleDeleteFromStorage,
    handleGenerateImage,
    generatingImage,
    handleLibraryConfirm,
    showLibrary,
    libraryTarget,
    openLibrary,
    closeLibrary,
    handleVariantPhotoChange,
    handleGenerateVariantImage,
    generatingVariantImage,
    handleGenerateVariantCopy,
    generatingVariantCopy,
    venuePlaceId,
    pricingUnlocked,
    setPricingUnlocked,
    pricingDetailsOpen,
    setPricingDetailsOpen,
    savedEventDate,
    currentEventDate,
    showDateChangeNotice,
    placePicked,
    showHostPinPicker,
    setShowHostPinPicker,
    showCancelWizard,
    setShowCancelWizard,
    handleHostPinConfirm,
    handleHostPinClear,
    handleVenueChange,
    handleVenueSelect,
    venueSuggestions,
    showVenueSuggestions,
    setShowVenueSuggestions,
    handleAddressChange,
    handleAddressSelect,
    addressSuggestions,
    showAddressSuggestions,
    setShowAddressSuggestions,
    showSettings,
    setShowSettings,
    vendors,
    loadingVendors,
    onSessionTimeChange,
    onSitterSizeChange,
    onMoverSizeChange,
    submitting,
    progressMessage,
    dateNotify,
    notifyDraft,
    setNotifyDraft,
    setDateNotify,
    sendingNotify,
    handleUncancelEvent,
    handleDeleteEvent,
    handleSaveAndNotifyDateChange,
    handleSubmit,
    handleRegenerateVideo,
    regeneratingVideo,
  } = useEventForm({ sourceSuggestionId, sourceRequestId, photoGateBlocked });

  const { recreateListing, recreatingListing } = useRecreateListing(eventData?.id);

  const navigate = useNavigate();

  // GIS-222. A co-host edits the event but not its price, whether it is live, or
  // whether it happens at all; those stay with the host. A copy would be created
  // under the host's name, which a co-host can't do either.
  const coHostOnly =
    !!eventData?.id &&
    eventAccessFor(eventData, { uid: globalState?.vendorID, isSuperAdmin: globalState?.isSuperAdmin }).coHostOnly;

  // Offered only once the event exists, has a photo to animate, and has the
  // opt-in saved on the doc, and never while a clip is already on its way — the
  // callable refuses all of those anyway, so showing the action would just hand
  // the operator an error. Reading videoEnabled off eventData rather than the
  // form matters: ticking the box does nothing until it is saved, and making a
  // video costs money, so it stays a saved decision.
  const videoInFlight = regeneratingVideo ||
    eventData?.videoStatus === "queued" ||
    eventData?.videoStatus === "generating";
  const canRegenerateVideo = Boolean(
    eventData?.id &&
    eventData?.photos?.[0] &&
    eventData?.videoEnabled === true &&
    !videoInFlight
  );

  // Copies the event as last saved, not the unsaved edits sitting in the form,
  // so "Duplicate" means the same thing here as it does on the events list.
  const handleDuplicateEvent = () => {
    if (!eventData?.id) return;
    navigate("/events/new", { state: buildEventDuplicateState(eventData) });
  };

  if (loading) {
    return <p>Loading...</p>;
  }

  return (
    <div>
      <form onSubmit={handleSubmit}>
        <div className="space-y-4">
          <EventUrlInput eventId={event.id} />
          <PhotosInput
            photos={event.photos}
            onPhotoChange={handlePhotoChange}
            removePhoto={removePhoto}
            onDeleteFromStorage={handleDeleteFromStorage}
            onSelectFromLibrary={() => openLibrary("event")}
            onGenerateImage={handleGenerateImage}
            generatingImage={generatingImage}
          />
          <EventVideoControl
            enabled={event.videoEnabled}
            onEnabledChange={(e) =>
              handleChange("videoEnabled", e.target.checked)
            }
            videoStatus={eventData?.videoStatus}
            videoUrl={eventData?.videoUrl}
            videoError={eventData?.videoError}
            onRegenerate={handleRegenerateVideo}
            canRegenerate={canRegenerateVideo}
          />
          {/* One modal, two destinations. `libraryTarget` decides which, and
              `existingPhotos` hides whatever that target already uses: the whole
              strip for the event, just its own photo for the second listing. */}
          <GlobalPhotosLibraryModal
            show={showLibrary}
            onClose={closeLibrary}
            onConfirm={handleLibraryConfirm}
            existingPhotos={
              libraryTarget === "variant"
                ? [event.listingVariants?.[VARIANT_ID]?.photo].filter(
                    (photo) => typeof photo === "string" && photo
                  )
                : event.photos
            }
            placeId={venuePlaceId}
            venueName={event.hostname}
            venueAddress={event.address}
          />
          <div className="flex flex-col lg:space-y-0">
            <div className="w-full ">
              {coHostOnly && (
                <p className="mb-3 w-fit max-w-sm rounded-md bg-gray-50 dark:bg-gray-800/40 ring-1 ring-gray-200 dark:ring-gray-700 px-3 py-2 text-sm text-gray-600 dark:text-gray-300">
                  Pricing is set by the event's host.
                </p>
              )}
              {!pricingUnlocked && !coHostOnly && (
                <div className="mb-3 w-fit max-w-sm rounded-md bg-gray-50 dark:bg-gray-800/40 ring-1 ring-gray-200 dark:ring-gray-700 px-3 py-2">
                  <div className="flex items-start gap-2">
                    <span className="flex h-4 w-4 flex-shrink-0 items-center justify-center rounded-full bg-gray-200 text-gray-600 dark:bg-gray-700 dark:text-gray-300 text-[10px] font-bold mt-0.5">
                      $
                    </span>
                    <div className="flex-1">
                      <div className="flex items-center justify-between gap-2">
                        <p className="flex items-center gap-1 text-sm font-medium text-gray-600 dark:text-gray-300">
                          Recommended pricing
                          <LockClosedIcon className="h-3.5 w-3.5" />
                        </p>
                        <button
                          type="button"
                          onClick={() => setPricingDetailsOpen((v) => !v)}
                          className="flex items-center gap-1 text-xs font-medium text-gray-500 dark:text-gray-400 hover:underline focus:outline-none"
                        >
                          {pricingDetailsOpen ? "See less" : "See more"}
                          <svg
                            className={`w-3.5 h-3.5 transform transition-transform ${
                              pricingDetailsOpen ? "rotate-180" : ""
                            }`}
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M19 9l-7 7-7-7"
                            />
                          </svg>
                        </button>
                      </div>
                      {pricingDetailsOpen && (
                        <>
                          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                            We recommend using our default pricing. You can
                            change it if you know what you're doing, but changes
                            are at your own risk.
                          </p>
                          <button
                            type="button"
                            onClick={() => setPricingUnlocked(true)}
                            className="mt-2 inline-flex items-center gap-1 text-xs font-medium text-gray-600 dark:text-gray-300 hover:underline"
                          >
                            <LockOpenIcon className="h-3.5 w-3.5" />
                            Unlock at my own risk
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              )}
              {pricingUnlocked && !coHostOnly && (
                <>
                  <CostInput
                    value={event.cost}
                    onChange={(e) => handleChange("cost", e.target.value)}
                    onCommit={handleCostCommit}
                  />
                  <div className="mt-3">
                    <PriceScheduleInput
                      value={event.priceSchedule}
                      onChange={(tiers) => handleChange("priceSchedule", tiers)}
                      baseCost={event.cost}
                    />
                  </div>
                  <button
                    type="button"
                    onClick={() => setPricingUnlocked(false)}
                    className="mt-2 inline-flex items-center gap-1 text-xs font-medium text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
                  >
                    <LockClosedIcon className="h-3.5 w-3.5" />
                    Lock pricing
                  </button>
                </>
              )}
            </div>
            <div className="w-full ">
              <Input
                label="Name"
                value={event.name}
                type="text"
                onChange={(e) => handleChange("name", e.target.value)}
              />
            </div>
          </div>

          <EventDateInput
            selectedDate={event.date}
            savedDate={savedEventDate}
            onChange={(value) => handleChange("date", value)}
            isEditMode={!!eventData?.id}
            onNotifyDateChange={
              showDateChangeNotice
                ? () => {
                    setNotifyDraft(
                      buildDateChangeMessage(event.name, currentEventDate)
                    );
                    setDateNotify({ eventId: event.id, newDate: currentEventDate });
                  }
                : null
            }
          />

          {placePicked && (event.hostname || event.address) && (
            <VenueMapPreview
              venueName={event.hostname}
              address={event.address}
            />
          )}

          <HostPinInput
            hostPin={event.hostPin}
            onOpenPicker={() => setShowHostPinPicker(true)}
            onClear={handleHostPinClear}
          />

          {showHostPinPicker && (
            <HostPinPickerModal
              hostPin={event.hostPin}
              onConfirm={handleHostPinConfirm}
              onClose={() => setShowHostPinPicker(false)}
            />
          )}

          <div className="flex flex-col lg:flex-row lg:space-x-4 space-y-4 lg:space-y-0">
            {/* Venue Name with Places autocomplete */}
            <div
              className="w-full lg:w-1/2 relative"
              onBlur={(e) => {
                if (!e.currentTarget.contains(e.relatedTarget)) {
                  setShowVenueSuggestions(false);
                }
              }}
            >
              <Input
                label="Venue Name"
                value={event.hostname}
                type="text"
                onChange={handleVenueChange}
                onFocus={() =>
                  venueSuggestions.length > 0 && setShowVenueSuggestions(true)
                }
                autoComplete="off"
              />
              {showVenueSuggestions && (
                <PlacesDropdown
                  suggestions={venueSuggestions}
                  onSelect={handleVenueSelect}
                />
              )}
            </div>

            {/* Address with Places autocomplete */}
            <div className="w-full lg:w-1/2">
              <div className="flex items-center justify-between">
                <label
                  htmlFor="address"
                  className="block text-sm font-medium leading-6 text-gray-500 dark:text-gray-400"
                >
                  Address
                </label>
                <Field className="flex items-center gap-2">
                  <Label
                    as="span"
                    className="text-xs font-light text-gray-500 dark:text-gray-400"
                  >
                    Show on Approval
                  </Label>
                  <Switch
                    checked={event.isPrivate}
                    onChange={(checked) => handleChange("isPrivate", checked)}
                    className="group relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent bg-gray-200 dark:bg-gray-700 transition-colors duration-200 ease-in-out focus:outline-none data-[checked]:bg-primary dark:data-[checked]:bg-primary"
                  >
                    <span
                      aria-hidden="true"
                      className="pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white dark:bg-gray-800 shadow ring-0 transition duration-200 ease-in-out group-data-[checked]:translate-x-5"
                    />
                  </Switch>
                </Field>
              </div>
              <div
                className="mt-1 relative"
                onBlur={(e) => {
                  if (!e.currentTarget.contains(e.relatedTarget)) {
                    setShowAddressSuggestions(false);
                  }
                }}
              >
                <input
                  onChange={handleAddressChange}
                  onFocus={() =>
                    addressSuggestions.length > 0 &&
                    setShowAddressSuggestions(true)
                  }
                  id="address"
                  value={event.address}
                  name="address"
                  type="text"
                  required
                  autoComplete="off"
                  className="px-4 block w-full rounded-md border-0 py-1.5 text-gray-900 dark:text-gray-100 dark:bg-gray-800 shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-gray-600 placeholder:text-gray-400 dark:placeholder:text-gray-500 focus:ring-2 focus:ring-inset focus:ring-primary text-sm sm:text-base sm:leading-6"
                />
                {showAddressSuggestions && (
                  <PlacesDropdown
                    suggestions={addressSuggestions}
                    onSelect={handleAddressSelect}
                  />
                )}
              </div>
            </div>
          </div>

          <CapacityInput
            size={event.size}
            groupCount={event.groupCount}
            group={event.group}
            onSitterSizeChange={onSitterSizeChange}
            onMoverSizeChange={onMoverSizeChange}
          />

          <RoomKindInput category={event.category} field={event.field} onChange={handleChange} />

          <div>
            <label
              htmlFor="description"
              className="block text-sm font-medium text-gray-500 dark:text-gray-400"
            >
              Description
            </label>
            <textarea
              id="description"
              value={event.description}
              onChange={(e) => handleChange("description", e.target.value)}
              rows={15}
              className="text-xs mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 shadow-sm focus:border-primaryDark focus:ring-primaryDark sm:text-sm"
            ></textarea>
          </div>

          {/* Optional second listing creative. `listingVariants` is the
              author's map; the ids the sync writes back live in
              `listingSyncState`, which is read here and never written. */}
          <ListingVariantSection
            variants={event.listingVariants}
            onChange={(next) => handleChange("listingVariants", next)}
            syncState={eventData?.listingSyncState}
            eventName={event.name}
            eventDescription={event.description}
            eventPhoto={event.photos?.[0]}
            eventHasVideo={eventData?.videoStatus === "ready"}
            onGeneratePhoto={handleGenerateVariantImage}
            generatingPhoto={generatingVariantImage}
            onGenerateCopy={handleGenerateVariantCopy}
            generatingCopy={generatingVariantCopy}
            onUploadPhoto={handleVariantPhotoChange}
            onSelectPhotoFromLibrary={() => openLibrary("variant")}
            onRecreate={recreateListing}
            recreating={recreatingListing}
            // Where the event's own listing made it to. A platform it never
            // reached is not offered for the second listing either.
            platformsInUse={["meetup", "eventbrite"].filter((p) => eventData?.[`${p}EventId`])}
          />

          <div className="p-4 bg-slate-50 dark:bg-slate-900 border border-stone-300 dark:border-stone-700 rounded-md">
            <button
              type="button"
              onClick={() => setShowSettings(!showSettings)}
              aria-expanded={showSettings}
              className="w-full flex items-center justify-between text-left focus:outline-none"
            >
              <label className="flex items-center gap-2 text-md font-medium text-gray-500 dark:text-gray-400">
                <Cog6ToothIcon className="w-5 h-5 shrink-0" aria-hidden="true" />
                Settings
              </label>
              <svg
                className={`w-5 h-5 transform transition-transform ${showSettings ? "rotate-180" : ""
                  }`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 9l-7 7-7-7"
                />
              </svg>
            </button>
            {showSettings && (
              <div className="mt-4 space-y-4">
                {globalState?.isSuperAdmin && (
                  <>
                    <VendorSelector
                      vendors={vendors}
                      loadingVendors={loadingVendors}
                      value={event.vendorID}
                      onChange={(vendorID) =>
                        handleChange("vendorID", vendorID)
                      }
                    />
                    <GroupNamesInput
                      group={event.group}
                      onSittersChange={(e) =>
                        handleNestedChange("group", "sitters", e.target.value)
                      }
                      onMoversChange={(e) =>
                        handleNestedChange("group", "movers", e.target.value)
                      }
                      event={event}
                      handleChange={handleChange}
                    />
                  </>
                )}

                <CheckInControl
                  requireCheckIn={event.requireCheckIn}
                  onRequireCheckInChange={(e) =>
                    handleChange("requireCheckIn", e.target.checked)
                  }
                  duration={event.duration}
                  onDurationChange={(value) => handleChange("duration", value)}
                />

                {/* Round settings only mean something on a mixer. */}
                {event.requireCheckIn !== false && (
                <>
                <ButtonGroup
                  buttons={SESSION_TIME_BTNS}
                  onSelected={onSessionTimeChange}
                  defaultSelected={event.sessionTime}
                  label="Round Length"
                />
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-500 dark:text-gray-400">
                    Max Rounds / Total Time
                  </label>
                  <div className="flex items-start gap-4">
                    <StepperInput
                      initialValue={event.maxRound}
                      onChange={(value) => handleChange("maxRound", value)}
                      min={1}
                      max={30}
                      step={1}
                    />
                    <div className="space-y-1 flex-1">
                      <div className="bg-primary text-white p-2 rounded-lg w-24 sm:w-1/3 h-[50px] border shadow-sm flex items-center justify-center text-sm ">
                        {(() => {
                          const maxRound = event.maxRound || 0;
                          const sessionTime = event.sessionTime || 0;
                          const totalSeconds = maxRound * sessionTime;
                          const hours = Math.floor(totalSeconds / 3600);
                          const minutes = Math.floor(
                            (totalSeconds % 3600) / 60
                          );
                          return (
                            <div className="flex items-center">
                              <svg
                                className="w-4 h-4 mr-2"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                                />
                              </svg>
                              <span className="font-medium">
                                {hours}h {minutes}m
                              </span>
                            </div>
                          );
                        })()}
                      </div>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-500 dark:text-gray-400">
                      Delay Start (seconds)
                    </label>
                    <StepperInput
                      initialValue={event.delayStart}
                      onChange={(value) => handleChange("delayStart", value)}
                      min={30}
                      max={900}
                      step={30}
                    />
                  </div>
                </div>
                </>
                )}

                <MeetupEventIdInput
                  value={event.meetupEventId}
                  onChange={(e) =>
                    handleChange("meetupEventId", e.target.value)
                  }
                />

                <StatusCheckboxes
                  isActive={event.isActive}
                  isCompleted={event.isCompleted}
                  onActiveChange={(e) =>
                    handleChange("isActive", e.target.checked)
                  }
                  onCompletedChange={(e) =>
                    handleChange("isCompleted", e.target.checked)
                  }
                  canToggleCompleted={!!globalState?.isSuperAdmin}
                  canToggleActive={!coHostOnly}
                />
              </div>
            )}
          </div>

          <FormActions
            isExistingEvent={eventData?.id}
            isCancelled={eventData?.status === "cancelled"}
            hasGuests={countEventGuests(eventData) > 0}
            onDuplicate={handleDuplicateEvent}
            onCancel={() => setShowCancelWizard(true)}
            onUncancel={handleUncancelEvent}
            onDelete={handleDeleteEvent}
            canDuplicate={!coHostOnly}
            canEndEvent={!coHostOnly}
            submitLabel={eventData?.id ? "Update Event" : "Create Event"}
            submitDisabled={photoGateBlocked}
            loading={submitting}
            progressMessage={progressMessage}
          />
        </div>
      </form>

      {dateNotify && (
        <DateChangeNotifyModal
          newDate={dateNotify.newDate}
          draft={notifyDraft}
          onDraftChange={setNotifyDraft}
          onSend={handleSaveAndNotifyDateChange}
          onClose={() => setDateNotify(null)}
          sending={sendingNotify}
        />
      )}

      {showCancelWizard && (
        <CancelEventWizard
          eventData={eventData}
          allowAnyEvent={!!globalState?.hasSuperAdminClaim}
          onClose={() => setShowCancelWizard(false)}
        />
      )}
    </div>
  );
};
